import { Component, inject, Input, OnChanges } from '@angular/core';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonLabel,
  IonRow,
  IonText,
} from '@ionic/angular/standalone';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import {
  IMAGE_SRC,
  NO_RESULTS,
} from 'src/app/components/shared/shared-constants';
import { PackageInquiry } from 'src/app/core/models/package-tracking.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { NoResultComponent } from '../no-result/no-result.component';
import { AccesorialCardComponent } from './accesorial-card/accesorial-card.component';
import { DetailsCardComponent } from './details-card/details-card.component';
import {
  ACCESSORIALS_SECTION_ROWS,
  AccessorialsSectionData,
  CONSTS,
  DEST_SECTION_ROWS,
  DestSectionData,
  HAZMAT_SECTION_ROWS,
  HazmatSectionData,
  ORIGIN_SECTION_ROWS,
  OriginSectionData,
  TIME_SECTION_ROWS,
  TimeSectionData,
  TOP_SECTION_ROWS,
  TopSectionData,
} from './package-inquiry.constants';
import { TravelSectionComponent } from './travel-section/travel-section.component';

/**
 * PackageInquiryComponent
 *
 * Renders a printable **Package Inquiry** view composed of multiple sections
 * (Top, Travel, Origin, Destination, Accessorials, Hazmat, Time) and
 * provides an **Export to PDF** action.
 *
 * #### Key behaviors
 * - When `detailsData` changes, each section is (re)built from the model.
 * - `exportToPdf()` captures `#contentToPrint` with html2canvas and saves a
 *   pixel-perfect PDF using jsPDF. A temporary `.export-cleanup` CSS class is
 *   applied to neutralize Ionic backgrounds/shadows to avoid gray artifacts.
 * - A global loading modal is shown via `CommonUtils` while the export runs.
 *
 * #### Dependencies
 * - **html2canvas**: DOM -> bitmap
 * - **jsPDF**: bitmap -> PDF file
 * - **Ionic standalone components**: grid/typography
 *
 * #### CSS requirement
 * Add an `.export-cleanup` rule in a global stylesheet to ensure a pure-white
 * print and to remove box-shadows/gradients during capture. See README notes
 * at the bottom of this file for a template.
 */
@Component({
  selector: 'app-package-inquiry',
  templateUrl: './package-inquiry.component.html',
  imports: [
    TravelSectionComponent,
    NoResultComponent,
    AccesorialCardComponent,
    DetailsCardComponent,
    IonCol,
    IonRow,
    IonGrid,
    IonLabel,
    IonText,
    IonButton,
  ],
  styleUrls: ['./package-inquiry.component.scss'],
})
export class PackageInquiryComponent implements OnChanges {
  /**
   * The end-to-end package inquiry payload to display.
   * When provided/changed, the component maps it into the individual
   * sections' `IOption[]` data structures for rendering.
   */
  @Input() detailsData?: PackageInquiry;

  /** Utility for app-wide helpers (e.g., loading modal). */
  readonly commonUtils = inject(CommonUtils);

  /** View-models for each rendered section */
  topData!: IOption[];
  origData!: IOption[];
  destData!: IOption[];
  accessorialsData!: IOption[];
  hazmatData!: IOption[];
  timeData!: IOption[];

  /** Short, human-friendly addresses for the travel section. */
  origAddress!: string;
  destAddress!: string;

  /** View constants / assets */
  readonly IMAGES = IMAGE_SRC;
  readonly NO_RESULTS = NO_RESULTS;
  readonly CONSTANTS = CONSTS;

  get latestSlicCode(): string {
    return this.detailsData?.latestTracknum?.[0]?.slic?.trim() ?? '';
  }

  /**
   * Reactively maps the inbound `detailsData` into section view-models.
   * Called automatically by Angular whenever `@Input() detailsData` changes.
   */
  ngOnChanges(): void {
    if (this.detailsData !== undefined) {
      this.mapTopSection(this.detailsData);
      this.mapTravelSection(this.detailsData);
      this.mapOriginSection(this.detailsData);
      this.mapDestSection(this.detailsData);
      this.mapAccessorialsSection(this.detailsData);
      this.mapHazmatSection(this.detailsData);
      this.mapTimeSection(this.detailsData);
    }
  }

  /**
   * Export the `#contentToPrint` DOM to a PDF file.
   *
   * Steps:
   * 1. Show a blocking loading modal via `CommonUtils`.
   * 2. Add `.export-cleanup` to force a white, shadow-less print surface.
   * 3. Await one animation frame so the loader/styles paint before capture.
   * 4. Use html2canvas to render the element to a canvas bitmap.
   * 5. Add the PNG to a jsPDF instance sized to the canvas and save.
   * 6. Remove the cleanup class and hide the loading modal.
   *
   * Notes:
   * - `backgroundColor` is set to `#ffffff` to avoid transparency artifacts.
   * - `scale` uses a capped DPR to balance quality and performance.
   * - `onclone` applies the `.export-cleanup` style to the cloned DOM to
   *   ensure consistent rendering in html2canvas’ isolation.
   */
  async exportToPdf() {
    // 1) Show loader and lock the modal
    this.commonUtils.updateLoadingModal(true);
    this.commonUtils.lockUpdateLoadingModal(true);

    // 2) Target the exact container printed in the template
    const el = document.getElementById('contentToPrint') as HTMLElement;
    if (!el) return;

    // Apply capture-only cleanup styles (removed in finally)
    el.classList.add('export-cleanup');

    // 3) Let the browser paint the loader + styles before heavy work begins
    await new Promise((r) => requestAnimationFrame(() => r(null)));

    try {
      // 4) Render DOM to canvas (TS-safe `backgroundColor`)
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 2),
        useCORS: true,
        removeContainer: true,
        logging: false,
        onclone: (doc) => {
          const clone = doc.getElementById('contentToPrint');
          if (clone) clone.classList.add('export-cleanup');
        },
      });

      // 5) Save as PDF, matching the canvas size
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
        hotfixes: ['px_scaling'],
      });
      pdf.addImage(img, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save('package-inquiry.pdf');
    } finally {
      // 6) Always cleanup styles and loader, even if export fails
      el.classList.remove('export-cleanup');
      this.commonUtils.lockUpdateLoadingModal(false);
      this.commonUtils.updateLoadingModal(false);
    }
  }

  /**
   * Build the "Top" section from the domain model into `IOption[]`.
   */
  private mapTopSection(data: PackageInquiry) {
    const topData: TopSectionData = {
      trackingNo: data.originDetails[0]?.trackingNo ?? '',
      atn: data.originDetails[0]?.altTracknum ?? '',
      serviceCode: data.originDetails[0]?.serviceTypeCode ?? '',
      serviceMnemonic: data.originDetails[0]?.serviceTypeMnemonic ?? '',
      dimensions: data.destDetails[0]?.dimensions ?? '',
      weight: data.destDetails[0]?.weight ?? '',
      weightUnit: data.destDetails[0]?.weightUnit ?? '',
      density: data.destDetails[0]?.density ?? '',
    };

    const dataVals = Object.values(topData) as string[];
    this.topData = TOP_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }

  /**
   * Build the "Travel" section addresses.
   */
  private mapTravelSection(data: PackageInquiry) {
    this.origAddress = data.originDetails[0]?.originAddress ?? '';
    this.destAddress = data.destDetails[0]?.address ?? '';
  }

  /**
   * Build the "Origin" section into `IOption[]`.
   */
  private mapOriginSection(data: PackageInquiry) {
    const origData: OriginSectionData = {
      shipperName: data.originDetails[0]?.shipperName ?? '',
      shipperNumber: data.originDetails[0]?.shipper_number ?? '',
      originAddress: data.originDetails[0]?.originAddress ?? '',
      postalCode: data.originDetails[0]?.postalCode ?? '',
      countryCode: data.destDetails[0]?.countryCode ?? '',
      originSlic: data.originDetails[0]?.originSLIC ?? '',
      pickupDate: data.originDetails[0]?.pickupDate ?? '',
      sdd: data.originDetails[0]?.SDD ?? '',
      rsdd: data.originDetails[0]?.RSDD ?? '',
      rdd: data.originDetails[0]?.RDD ?? '',
    };

    const dataVals = Object.values(origData) as string[];
    this.origData = ORIGIN_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }

  /**
   * Build the "Destination" section into `IOption[]`.
   */
  private mapDestSection(data: PackageInquiry) {
    const destData: DestSectionData = {
      consigneeName: data.destDetails[0]?.consigneeName ?? '',
      consigneePhone: data.destDetails[0]?.consigneePhone ?? '',
      deliveryAddress: data.destDetails[0]?.deliveryAddress ?? '',
      postalCode: data.destDetails[0]?.postalCode ?? '',
      countryCode: data.destDetails[0]?.countryCode ?? '',
      destHubSlic: data.destDetails[0]?.destinationHubSLIC ?? '',
      consigneeEmail: data.destDetails[0]?.consigneeEmail ?? '',
      destCenterSlic: data.destDetails[0]?.destinationCenterSLIC ?? '',
    };

    const dataVals = Object.values(destData) as string[];
    this.destData = DEST_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }

  /**
   * Build the "Accessorials" section into `IOption[]`.
   */
  private mapAccessorialsSection(data: PackageInquiry) {
    const accessData: AccessorialsSectionData = {
      codAmount: data.accessorials[0]?.CODAmount ?? '',
      codCurrency: data.accessorials[0]?.CODCurrency ?? '',
      CODIndicator: data.accessorials[0]?.CODIndicator === '1' ? 'Y' : 'N',
      icodAmount: data.accessorials[0]?.ICODAmount ?? '',
      icodCurrency: data.accessorials[0]?.ICODCurrency ?? '',
      ICODIndicator: data.accessorials[0]?.ICODIndicator === '1' ? 'Y' : 'N',
      packageVoid: data.accessorials[0]?.packageVoid ?? '',
      adlEligible: data.accessorials[0]?.ADLEligible ?? '',
      customerChoice: data.accessorials[0]?.myChoiceCustomer ?? '',
      sigRequired: data.accessorials[0]?.signatureRequired ?? '',
      adultSigRequired: data.accessorials[0]?.adultSignatureRequired ?? '',
      cdiActive: data.accessorials[0]?.CDIactive ?? '',
      interceptActive: data.accessorials[0]?.interceptActive ?? '',
      pdiActive: data.accessorials[0]?.PDIActive ?? '',
      infoNotice1: data.originDetails[0]?.infoNoticeNumber1 ?? '',
      infoNotice2: data.originDetails[0]?.infoNoticeNumber2 ?? '',
      infoNotice3: data.originDetails[0]?.infoNoticeNumber3 ?? '',
      accessorials: data.accessorials[0]?.accessorials ?? '',
    };

    const dataVals = Object.values(accessData) as string[];

    this.accessorialsData = ACCESSORIALS_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }

  /**
   * Build the "Hazmat Accessorials" section into `IOption[]`.
   */
  private mapHazmatSection(data: PackageInquiry) {
    const hazmatData: HazmatSectionData = {
      hazmat: data.hazmatAccessorials[0]?.HAZMAT ?? '',
      regQuantity: data.hazmatAccessorials[0]?.regQuantityCode ?? '',
      caPackage: data.hazmatAccessorials[0]?.CAPackage ?? '',
      adrPackageInd: data.hazmatAccessorials[0]?.ADRPackageIndicator ?? '',
      adrPoints: data.hazmatAccessorials[0]?.ADRPoints ?? '',
      trans0: data.hazmatAccessorials[0]?.transCat0Units ?? '',
      trans1: data.hazmatAccessorials[0]?.transCat1Units ?? '',
      trans2: data.hazmatAccessorials[0]?.transCat2Units ?? '',
      trans3: data.hazmatAccessorials[0]?.transCat3Units ?? '',
      trans4: data.hazmatAccessorials[0]?.transCat4Units ?? '',
    };

    const dataVals = Object.values(hazmatData) as string[];
    this.hazmatData = HAZMAT_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }

  /**
   * Build the "Times" section into `IOption[]`.
   */
  private mapTimeSection(data: PackageInquiry) {
    const timeData: TimeSectionData = {
      insertTime: data.originDetails[0]?.insertTime ?? '',
      updateTime: data.originDetails[0]?.updateTime ?? '',
      msgTime: data.originDetails[0]?.messageTime ?? '',
    };

    const dataVals = Object.values(timeData) as string[];
    this.timeData = TIME_SECTION_ROWS.map((label, i) => ({
      label,
      value: dataVals[i],
    }));
  }
}
